import { BookOpen, TrendingUp, Building2 } from 'lucide-react';
import SlideShell from './SlideShell';

interface NextStepsContent {
  suggested_lessons?: string[];
  go_deeper?: string[];
  build_foundation?: string[];
  coaching_note?: string;
}

interface NextStepsSlideProps {
  slideNumber: number;
  totalSlides: number;
  title: string;
  subtitle: string;
  content: NextStepsContent;
  lessonTitle?: string;
  lessonThumbnail?: string;
  onNext?: () => void;
  onBack?: () => void;
  onBackToLesson?: () => void;
  isCompleted?: boolean;
  onMarkComplete?: () => void;
}

const NextStepsSlide = ({
  slideNumber,
  totalSlides,
  title,
  subtitle,
  content,
  lessonTitle,
  lessonThumbnail,
  onNext,
  onBack,
  onBackToLesson,
  isCompleted,
  onMarkComplete,
}: NextStepsSlideProps) => {
  return (
    <SlideShell
      lessonTitle={lessonTitle}
      lessonThumbnail={lessonThumbnail}
      slideNumber={slideNumber}
      totalSlides={totalSlides}
      subtitle={subtitle}
      onBackToLesson={onBackToLesson}
      onBack={onBack}
      onNext={onNext}
      showMarkComplete={!!onMarkComplete}
      isCompleted={isCompleted}
      onMarkComplete={onMarkComplete}
    >
      <h1 className="font-heading text-2xl mb-6 text-foreground">
        {title}
      </h1>

      {/* Coaching Note */}
      {content.coaching_note && (
        <div className="bg-amber-50 border border-amber-200 rounded-[15px] p-5 mb-8">
          <p className="text-sm text-foreground leading-relaxed">
            {content.coaching_note}
          </p>
        </div>
      )}

      <div className="space-y-6">
        {/* Suggested Lessons */}
        {content.suggested_lessons && content.suggested_lessons.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-medium text-foreground">
                Suggested Next Lessons
              </h2>
            </div>
            <ul className="space-y-2">
              {content.suggested_lessons.map((lesson, idx) => (
                <li
                  key={idx}
                  className="text-sm text-foreground bg-white rounded-[15px] border border-[#f0f0f0] p-3 hover:bg-[#fafafa] transition-colors"
                >
                  • {lesson}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Go Deeper */}
        {content.go_deeper && content.go_deeper.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-medium text-foreground">
                Go Deeper
              </h2>
            </div>
            <ul className="space-y-2">
              {content.go_deeper.map((topic, idx) => (
                <li
                  key={idx}
                  className="text-sm text-foreground bg-white rounded-[15px] border border-[#f0f0f0] p-3 hover:bg-[#fafafa] transition-colors"
                >
                  • {topic}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Build Foundation */}
        {content.build_foundation && content.build_foundation.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-5 h-5 text-orange-600" />
              <h2 className="text-lg font-medium text-foreground">
                Build Foundation
              </h2>
            </div>
            <ul className="space-y-2">
              {content.build_foundation.map((topic, idx) => (
                <li
                  key={idx}
                  className="text-sm text-foreground bg-white rounded-[15px] border border-[#f0f0f0] p-3 hover:bg-[#fafafa] transition-colors"
                >
                  • {topic}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </SlideShell>
  );
};

export default NextStepsSlide;
